import React, { useEffect, useState } from 'react';
import {
  Modal,
  IconButton,
  Box,
  Typography,
  TextField,
  Button,
  DialogContent,
  Dialog,
  Select,
  MenuItem,
  SelectChangeEvent,
  DialogTitle,
  Grid
} from '@mui/material';
import { InventoryDetails } from 'src/models/Inventory';
import { InventoryService } from 'src/services/InventoryService';
import { toast } from 'react-toastify';
import CloseIcon from '@mui/icons-material/Close';
import Swal from 'sweetalert2';
import { InventoryCategories } from 'src/models/InventoryCategory';
import { Field, Form, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import { AuthService } from 'src/services/AuthService';

export interface ProductProps {
  open: boolean;
  onClose: () => void;
  shopId: string;
  userId: string;
  inventory: any;
  setInventory: any;
}

export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 5 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

const AddProduct: React.FC<ProductProps> = ({
  open,
  onClose,
  shopId,
  userId,
  inventory,
  setInventory
}) => {
  const [categories, setCategories] = useState<
    { _id: string; categoryName: string }[]
  >([]);
  const [branches, setBranches] = useState<
    { _id: string; branchName: string }[]
  >([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [newCategoryName, setNewCategoryName] = useState<string>('');
  const [categoryModalOpen, setCategoryModalOpen] = useState<boolean>(false);
  const [quantities, setQuantities] = useState<
    { branchId: string; quantity: number }[]
  >([]);

  useEffect(() => {
    getAllCategories();
    getAllBranches();
  }, [shopId]);

  const getAllCategories = () => {
    InventoryService.getCategories(shopId, '').then((res) => {
      if (res.success) {
        const categoryList = res.data.categories || [];
        setCategories(categoryList);
      }
    });
  };

  const getAllBranches = async () => {
    try {
      const res = await AuthService.getShopBranchesByShopId(shopId);
      if (res.success) {
        setBranches(res.data || []);
        setQuantities([
          { branchId: 'default_branch', quantity: 0 },
          ...res.data.map((branch: any) => ({
            branchId:
              branch._id || `branch_${Math.random().toString(36).substr(2, 9)}`,
            quantity: 0
          }))
        ]);
      }
    } catch (error) {
      console.error('Failed to fetch shop branch details:', error);
      setBranches([]);
    }
  };

  // const handleQuantityChange = (branchId: string, value: string) => {
  //   const quantity = parseInt(value) || 0;
  //   setQuantities((prev) => {
  //     const existing = prev.find((item) => item.branchId === branchId);
  //     if (existing) {
  //       return prev.map((item) =>
  //         item.branchId === branchId ? { ...item, quantity } : item
  //       );
  //     }
  //     return [...prev, { branchId, quantity }];
  //   });
  // };
  const handleQuantityChange = (branchId: string, value: string) => {
    setQuantities((prevQuantities) => {
      const exists = prevQuantities.some((item) => item.branchId === branchId);
      const newQuantity = value === '' ? undefined : Number(value);

      if (exists) {
        return prevQuantities.map((item) =>
          item.branchId === branchId ? { ...item, quantity: newQuantity } : item
        );
      } else {
        return [...prevQuantities, { branchId, quantity: newQuantity }];
      }
    });
  };

  const handleSubmit = (values: any, { resetForm }) => {
    const branchQuantities = quantities.map((q) => ({
      branchId: q.branchId === 'default_branch' ? 'default_branch' : q.branchId,
      quantity: q.quantity
    }));

    const data: InventoryDetails = {
      shopId: shopId,
      userId: userId,
      inventoryCategoryId: values.selectedCategory,
      createdBy: userId,
      productName: values.productName,
      sKUNumber: values.skuNumber,
      brandName: values.brandName,
      productPrice: parseFloat(values.productPrice),
      price: parseFloat(values.productSellingPrice),
      quantity: values.productQuantity || 0,
      productType: values.productType,
      branchQuantities: branchQuantities,
      productDiscount: parseFloat(values.productDiscount) || 0,
      discountType: values.discountType,
      hasCompleted: 'COMPLETED'
    };

    InventoryService.addProduct(data).then((res) => {
      if (res.success) {
        Swal.fire('Product Added Successfully');
        const updatedInventoryList = [res.data, ...inventory];
        setInventory(updatedInventoryList);
        resetForm();
        setQuantities([]);
        onClose();
      } else {
        onClose();
        Swal.fire({
          icon: 'error',
          title: 'Failed to Add Product',
          confirmButtonColor: '#FD7F00'
        });
      }
    });
  };

  const formik = useFormik({
    initialValues: {
      productName: '',
      skuNumber: '',
      brandName: '',
      productPrice: '',
      productSellingPrice: '',
      productQuantity: '',
      selectedCategory: '',
      productType: 'Units',
      productDiscount: '',
      discountType: 'PERCENTAGE'
    },
    validationSchema: Yup.object({
      productName: Yup.string().required('Product Name is required'),
      skuNumber: Yup.string().required('SKU Number is required'),
      brandName: Yup.string().required('Brand Name is required'),
      productPrice: Yup.number()
        .required('Product Price is required')
        .positive('Price must be positive'),
      productSellingPrice: Yup.number()
        .required('Product Selling Price is required')
        .positive('Price must be positive'),
      productQuantity: Yup.number().when('$branches', {
        is: (branches: any[]) => branches?.length === 0,
        then: Yup.number()
          .required('Product Quantity is required')
          .positive('Quantity must be positive')
          .integer('Quantity must be an integer'),
        otherwise: Yup.number().nullable()
      }),
      selectedCategory: Yup.string().required('Category is required'),
      productType: Yup.string().required('Product Type is required'),
      productDiscount: Yup.number()
        .min(0, 'Discount cannot be negative')
        .nullable()
        .transform((v, o) => (o === '' ? 0 : v)),
      discountType: Yup.string().required('Discount Type is required')
    }),
    onSubmit: handleSubmit
  });

  const handleAddCategory = () => {
    const data: InventoryCategories = {
      shopId: shopId,
      categoryName: newCategoryName,
      categoryColor: '64aa06',
      isPinCategory: false,
      createdBy: userId
    };

    InventoryService.createInventoryCategory(data).then((res) => {
      if (res.success) {
        Swal.fire({
          customClass: { container: 'my-swal' },
          position: 'top-end',
          icon: 'success',
          title: 'New Category Added Successfully',
          showConfirmButton: false,
          timer: 1500
        });
        setCategories([...categories, res.data]);
        formik.setFieldValue('selectedCategory', res.data._id);
        setNewCategoryName('');
        setCategoryModalOpen(false);
      } else {
        setNewCategoryName('');
        setCategoryModalOpen(false);
        Swal.fire({
          position: 'top-end',
          icon: 'error',
          title: 'New Category Added Fail',
          showConfirmButton: false,
          timer: 1500
        });
      }
    });
  };

  const handleCategoryChange = (event: SelectChangeEvent<string>) => {
    if (event.target.value === 'add-new-category') {
      setCategoryModalOpen(true);
    } else {
      formik.setFieldValue('selectedCategory', event.target.value);
    }
  };

  const handleClose = () => {
    formik.resetForm();
    setQuantities([]);
    onClose();
  };

  return (
    <div>
      <Dialog
        onClose={handleClose}
        open={open}
        aria-labelledby="customized-dialog-title"
        fullWidth
        maxWidth="md"
      >
        <BootstrapDialogTitle
          id="customized-dialog-title"
          onClose={handleClose}
        >
          <Typography variant="h4" fontWeight="bold">
            Add a New Product
          </Typography>
        </BootstrapDialogTitle>
        <DialogContent dividers>
          <FormikProvider value={formik}>
            <Form onSubmit={formik.handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Product Name"
                    margin="normal"
                    variant="outlined"
                    name="productName"
                    error={
                      formik.touched.productName &&
                      Boolean(formik.errors.productName)
                    }
                    helperText={
                      formik.touched.productName && formik.errors.productName
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="SKU Number"
                    margin="normal"
                    variant="outlined"
                    name="skuNumber"
                    error={
                      formik.touched.skuNumber &&
                      Boolean(formik.errors.skuNumber)
                    }
                    helperText={
                      formik.touched.skuNumber && formik.errors.skuNumber
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={Select}
                    fullWidth
                    displayEmpty
                    name="productType"
                    value={formik.values.productType}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.productType &&
                      Boolean(formik.errors.productType)
                    }
                  >
                    <MenuItem value="" disabled>
                      Select Product Type
                    </MenuItem>
                    <MenuItem value="Units">Units</MenuItem>
                    <MenuItem value="Kg">Kilograms</MenuItem>
                    <MenuItem value="Metre">Metre</MenuItem>
                    <MenuItem value="Litre">Liters</MenuItem>
                    <MenuItem value="Sqft">Sqft</MenuItem>
                  </Field>
                  {formik.touched.productType && formik.errors.productType && (
                    <Typography variant="body2" color="error">
                      {formik.errors.productType}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Brand Name"
                    margin="normal"
                    variant="outlined"
                    name="brandName"
                    error={
                      formik.touched.brandName &&
                      Boolean(formik.errors.brandName)
                    }
                    helperText={
                      formik.touched.brandName && formik.errors.brandName
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Product Price"
                    margin="normal"
                    variant="outlined"
                    type="number"
                    name="productPrice"
                    error={
                      formik.touched.productPrice &&
                      Boolean(formik.errors.productPrice)
                    }
                    helperText={
                      formik.touched.productPrice && formik.errors.productPrice
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Seles Price"
                    margin="normal"
                    variant="outlined"
                    type="number"
                    name="productSellingPrice"
                    error={
                      formik.touched.productSellingPrice &&
                      Boolean(formik.errors.productSellingPrice)
                    }
                    helperText={
                      formik.touched.productSellingPrice &&
                      formik.errors.productSellingPrice
                    }
                  />
                </Grid>
                {branches.length === 0 && (
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      fullWidth
                      label="Quantity"
                      margin="normal"
                      variant="outlined"
                      type="number"
                      name="productQuantity"
                      error={
                        formik.touched.productQuantity &&
                        Boolean(formik.errors.productQuantity)
                      }
                      helperText={
                        formik.touched.productQuantity &&
                        formik.errors.productQuantity
                      }
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Available Quantity - Main Branch"
                    margin="normal"
                    variant="outlined"
                    type="number"
                    name="default_branch"
                    value={
                      quantities.find(
                        (item) => item.branchId === 'default_branch'
                      )?.quantity ?? ''
                    }
                    onChange={(e) =>
                      handleQuantityChange('default_branch', e.target.value)
                    }
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                {branches.map((branch, index) => {
                  const branchId = branch._id || `branch_${index}`;
                  return (
                    <Grid item xs={12} key={branchId}>
                      <Field
                        as={TextField}
                        fullWidth
                        label={`Available Quantity - ${branch.branchName}`}
                        margin="normal"
                        variant="outlined"
                        type="number"
                        name={branchId}
                        value={
                          quantities.find((item) => item.branchId === branchId)
                            ?.quantity ?? ''
                        }
                        onChange={(e) =>
                          handleQuantityChange(branchId, e.target.value)
                        }
                        inputProps={{ min: 0 }}
                      />
                    </Grid>
                  );
                })}
                <Grid item xs={7}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Discount"
                    variant="outlined"
                    type="number"
                    name="productDiscount"
                    error={
                      formik.touched.productDiscount &&
                      Boolean(formik.errors.productDiscount)
                    }
                    helperText={
                      formik.touched.productDiscount &&
                      formik.errors.productDiscount
                    }
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={5}>
                  <Field
                    as={Select}
                    fullWidth
                    displayEmpty
                    name="discountType"
                    margin="normal"
                    value={formik.values.discountType}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.discountType &&
                      Boolean(formik.errors.discountType)
                    }
                  >
                    <MenuItem value="" disabled>
                      Select Discount Type
                    </MenuItem>
                    <MenuItem value="PERCENTAGE">Percentage</MenuItem>
                    <MenuItem value="PAYRATE">Payrate</MenuItem>
                  </Field>
                  {formik.touched.discountType && formik.errors.discountType && (
                    <Typography variant="body2" color="error">
                      {formik.errors.discountType}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={Select}
                    fullWidth
                    displayEmpty
                    name="selectedCategory"
                    value={formik.values.selectedCategory}
                    onChange={handleCategoryChange}
                    error={
                      formik.touched.selectedCategory &&
                      Boolean(formik.errors.selectedCategory)
                    }
                  >
                    <MenuItem value="" disabled>
                      Select Category
                    </MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category._id} value={category._id}>
                        {category.categoryName}
                      </MenuItem>
                    ))}
                    <MenuItem value="add-new-category">
                      <em>Add New Category</em>
                    </MenuItem>
                  </Field>
                  {formik.touched.selectedCategory &&
                    formik.errors.selectedCategory && (
                      <Typography variant="body2" color="error">
                        {formik.errors.selectedCategory}
                      </Typography>
                    )}
                </Grid>
                <Grid item xs={12}>
                  <Button
                    color="primary"
                    variant="contained"
                    type="submit"
                    fullWidth
                    sx={{ mt: 2 }}
                  >
                    Submit
                  </Button>
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </DialogContent>
      </Dialog>

      <Dialog
        open={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" component="h2">
            Add New Category
          </Typography>
          <TextField
            fullWidth
            label="Category Name"
            margin="normal"
            variant="outlined"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddCategory}
            sx={{ mt: 2 }}
          >
            Add Category
          </Button>
        </Box>
      </Dialog>
    </div>
  );
};

export default AddProduct;
